import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Alert,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { createLmsApi, getLmsErrorMessage, getLmsListItems } from "../../lms/api";
import { downloadQuizTemplate, parseQuizWorkbook } from "../../lms/quizExcel";
import { LMS_QUESTION_TYPES, statusColor } from "../../lms/constants";
import { LmsErrorAlert, LmsOwnershipTag, LmsPageHeader, canEditLmsRow, formatDateTime } from "../../lms/components/LmsPrimitives";

const { Text } = Typography;

function questionFormValue(question) {
  return {
    id: question?.id,
    questionText: question?.questionText || "",
    questionType: question?.questionType || "MCQ",
    correctAnswer: question?.correctAnswer || "",
    marks: question?.marks || 1,
    optionFields: Array.isArray(question?.options) ? question.options : ["", "", "", ""],
    order: question?.order || 0,
  };
}

function errorText(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(errorText).filter(Boolean).join(" ");
  if (value && typeof value === "object") return Object.values(value).map(errorText).filter(Boolean).join(" ");
  return "";
}

function questionPayload(question, quizID, index) {
  return {
    quizID,
    questionText: question.questionText,
    questionType: question.questionType,
    correctAnswer: String(question.correctAnswer || "").trim(),
    marks: question.marks || 1,
    options: question.questionType === "MCQ"
      ? (question.optionFields || []).map((option) => String(option || "").trim()).filter(Boolean)
      : [],
    order: index + 1,
  };
}

const LmsQuizzes = () => {
  const navigate = useNavigate();
  const api = useMemo(() => createLmsApi(navigate), [navigate]);
  const [quizForm] = Form.useForm();
  const [feedbackForm] = Form.useForm();
  const [quizzes, setQuizzes] = useState([]);
  const [contents, setContents] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [quizPagination, setQuizPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [attemptPagination, setAttemptPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [attemptFilters, setAttemptFilters] = useState({ quiz: undefined, studentName: undefined, status: undefined });
  const [studentQuery, setStudentQuery] = useState("");
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [activeTab, setActiveTab] = useState("quizzes");
  const [reviewAttempt, setReviewAttempt] = useState(null);
  const [markAttempt, setMarkAttempt] = useState(null);
  const [markValues, setMarkValues] = useState({});
  const [markOverride, setMarkOverride] = useState("");
  const [markSaving, setMarkSaving] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState({});
  const attemptRequestID = useRef(0);
  const [loading, setLoading] = useState(false);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [feedbackAttempt, setFeedbackAttempt] = useState(null);
  const [questionErrors, setQuestionErrors] = useState({});
  const [importedQuestions, setImportedQuestions] = useState([]);
  const [importError, setImportError] = useState("");
  const importInputRef = useRef(null);

  const loadQuizzes = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    const response = await api.quizzes.list({ page, pageSize, ordering: "-created_at" });
    if (response?.ok) {
      const result = getLmsListItems(response.data);
      setQuizzes(result.items);
      setQuizPagination({ current: page, pageSize, total: result.count });
    } else setError(getLmsErrorMessage(response, "Unable to load quizzes"));
    setLoading(false);
  }, [api]);

  const loadAttempts = useCallback(async (page = 1, pageSize = 20) => {
    const requestID = ++attemptRequestID.current;
    setAttemptsLoading(true);
    const params = { page, pageSize, student_name: attemptFilters.studentName, status: attemptFilters.status };
    const response = attemptFilters.quiz
      ? await api.quizzes.attempts(attemptFilters.quiz, params)
      : await api.quizAttempts.list({ ...params, ordering: "-started_at" });
    if (requestID !== attemptRequestID.current) return;
    if (response?.ok) {
      const result = getLmsListItems(response.data);
      setAttempts(result.items);
      setAttemptPagination({ current: page, pageSize, total: result.count });
      setError("");
    } else setError(getLmsErrorMessage(response, "Unable to load quiz attempts"));
    setAttemptsLoading(false);
  }, [api, attemptFilters]);

  const loadDependencies = useCallback(async () => {
    const response = await api.content.list({ page: 1, pageSize: 100, mine: true });
    if (response?.ok) setContents(getLmsListItems(response.data).items);
  }, [api]);

  useEffect(() => {
    loadDependencies();
    loadQuizzes(1, quizPagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDependencies]);

  useEffect(() => {
    loadAttempts(1, attemptPagination.pageSize);
    // A filter change starts a new first page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAttempts]);

  const openCreate = () => {
    setEditing(null);
    quizForm.resetFields();
    quizForm.setFieldsValue({ maxAttempts: 1, isAutoMarked: true, questions: [questionFormValue()] });
    setQuestionErrors({});
    setImportedQuestions([]);
    setImportError("");
    setFormOpen(true);
  };

  const openEdit = async (record) => {
    if (record?.mine === false) {
      message.error("Read-only: you did not create this quiz and have not inherited it.");
      return;
    }
    const response = await api.quizzes.get(record.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to load quiz"));
      return;
    }
    const detail = response.data;
    const content = contents.find((item) => item.title === detail.contentTitle);
    setEditing(detail);
    setQuestionErrors({});
    setImportedQuestions([]);
    setImportError("");
    quizForm.setFieldsValue({
      contentID: detail.contentID || content?.id,
      title: detail.title,
      timeLimitMinutes: detail.timeLimitMinutes,
      maxAttempts: detail.maxAttempts || 1,
      isAutoMarked: detail.isAutoMarked !== false,
      questions: (detail.questions || []).map(questionFormValue),
    });
    setFormOpen(true);
  };

  const saveQuestions = async (quizID, questions, existingQuestions = []) => {
    const existingIds = new Set(existingQuestions.map((question) => question.id));
    const submittedIds = new Set(questions.map((question) => question.id).filter(Boolean));
    for (const [index, question] of questions.entries()) {
      const body = questionPayload(question, quizID, index);
      const response = question.id
        ? await api.quizQuestions.update(question.id, body)
        : await api.quizQuestions.create(body);
      if (!response?.ok) return { ...response, failedQuestionIndex: index };
      if (!question.id && response.data?.id) quizForm.setFieldValue(["questions", index, "id"], response.data.id);
    }
    for (const id of existingIds) {
      if (!submittedIds.has(id)) {
        const response = await api.quizQuestions.remove(id);
        if (!response?.ok) return response;
      }
    }
    return { ok: true };
  };

  const handleSave = async (values) => {
    setQuestionErrors({});
    const invalid = {};
    for (const [index, question] of (values.questions || []).entries()) {
      const payload = questionPayload(question, null, index);
      if (payload.questionType === "MCQ" && (payload.options.length < 2 || !payload.options.includes(payload.correctAnswer))) {
        invalid[index] = `Question ${index + 1}: Correct answer must be one of at least two options for MCQ questions.`;
      } else if (payload.questionType === "True/False" && !["True", "False"].includes(payload.correctAnswer)) {
        invalid[index] = `Question ${index + 1}: Correct answer must be True or False.`;
      }
    }
    if (Object.keys(invalid).length) { setQuestionErrors(invalid); message.error(Object.values(invalid)[0]); return; }
    setLoading(true);
    const quizBody = {
      contentID: values.contentID,
      title: values.title,
      timeLimitMinutes: values.timeLimitMinutes ?? null,
      maxAttempts: values.maxAttempts || 1,
      isAutoMarked: values.isAutoMarked !== false,
      // The backend's create serializer exposes questions as read-only on the
      // parent resource, so questions are synced through /quiz-questions.
      questions: [],
    };
    const response = editing
      ? await api.quizzes.update(editing.id, quizBody)
      : await api.quizzes.create(quizBody);
    if (!response?.ok) {
      setLoading(false);
      message.error(getLmsErrorMessage(response, "Unable to save quiz"));
      return;
    }
    // Keep the created parent ID so retrying questions cannot duplicate the quiz.
    if (!editing) setEditing({ ...response.data, questions: [] });
    const questionResponse = await saveQuestions(response.data.id, values.questions || [], editing?.questions || []);
    setLoading(false);
    if (!questionResponse?.ok) {
      const errorMessage = errorText(questionResponse.data) || getLmsErrorMessage(questionResponse, "Question could not be saved");
      const numberedError = errorMessage.match(/Question\s+(\d+)/i);
      const errorIndex = Number.isInteger(questionResponse.failedQuestionIndex)
        ? questionResponse.failedQuestionIndex
        : numberedError ? Number(numberedError[1]) - 1 : -1;
      if (errorIndex >= 0) setQuestionErrors({ [errorIndex]: errorMessage });
      message.error(getLmsErrorMessage(questionResponse, "Quiz saved, but questions could not be synchronized"));
      return;
    }
    message.success(editing ? "Quiz updated" : "Quiz created");
    setFormOpen(false);
    setEditing(null);
    loadQuizzes(1, quizPagination.pageSize);
  };

  const handleQuestionImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      if (!/\.xlsx?$/i.test(file.name)) throw new Error("Choose an Excel workbook (.xlsx or .xls).");
      const questions = parseQuizWorkbook(await file.arrayBuffer());
      setImportedQuestions(questions);
      setImportError("");
    } catch (error) {
      setImportedQuestions([]);
      setImportError(error.message || "Unable to read the Excel workbook");
    }
  };

  const applyImportedQuestions = () => {
    const existing = quizForm.getFieldValue("questions") || [];
    const onlyQuestion = existing[0];
    const isEmptyPlaceholder = existing.length === 1 && !onlyQuestion.id &&
      !onlyQuestion.questionText?.trim() && !String(onlyQuestion.correctAnswer ?? "").trim() &&
      (onlyQuestion.marks ?? 1) === 1 && onlyQuestion.questionType === "MCQ" &&
      !(onlyQuestion.optionFields || []).some((option) => String(option ?? "").trim());
    const current = isEmptyPlaceholder ? [] : existing;
    const questions = importedQuestions.map((question, index) => ({
      ...question,
      order: current.length + index + 1,
    }));
    quizForm.setFieldsValue({ questions: [...current, ...questions] });
    setImportedQuestions([]);
    message.success(`${questions.length} questions added`);
  };

  const handleDelete = async (record) => {
    const response = await api.quizzes.remove(record.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to delete quiz"));
      return;
    }
    message.success("Quiz deleted");
    if (attemptFilters.quiz === record.id) {
      setAttemptFilters((current) => ({ ...current, quiz: undefined }));
      setSelectedQuiz(null);
    }
    loadQuizzes(1, quizPagination.pageSize);
  };

  const openFeedbackFor = (attempt) => {
    setFeedbackAttempt(attempt);
    feedbackForm.setFieldsValue({ teacherFeedback: attempt.teacherFeedback || "" });
  };

  const handleFeedback = async (values) => {
    const response = await api.quizAttempts.feedback(feedbackAttempt.id, values.teacherFeedback);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to save feedback"));
      return;
    }
    message.success("Feedback saved");
    if (reviewAttempt && reviewAttempt.id === feedbackAttempt.id) {
      setReviewAttempt({ ...reviewAttempt, teacherFeedback: response.data?.teacherFeedback ?? values.teacherFeedback });
    }
    if (markAttempt && markAttempt.id === feedbackAttempt.id) {
      setMarkAttempt({ ...markAttempt, teacherFeedback: response.data?.teacherFeedback ?? values.teacherFeedback });
    }
    setFeedbackAttempt(null);
    feedbackForm.resetFields();
    loadAttempts(attemptPagination.current, attemptPagination.pageSize);
  };

  const quizColumns = [
    { title: "Quiz", dataIndex: "title", key: "title", render: (value, record) => <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => openEdit(record)}>{value}</Button> },
    { title: "Content", dataIndex: "contentTitle", key: "contentTitle" },
    { title: "Questions", dataIndex: "questionCount", key: "questionCount" },
    { title: "Time limit", dataIndex: "timeLimitMinutes", key: "timeLimitMinutes", render: (value) => value ? `${value} min` : "No limit" },
    { title: "Attempts", dataIndex: "maxAttempts", key: "maxAttempts" },
    { title: "Auto-marked", dataIndex: "isAutoMarked", key: "isAutoMarked", render: (value) => <Tag color={value ? "green" : "default"}>{value ? "Yes" : "No"}</Tag> },
    { title: "Access", key: "access", render: (_, record) => <LmsOwnershipTag record={record} /> },
    { title: "Created", dataIndex: "createdAt", key: "createdAt", render: (value) => formatDateTime(value) },
    { title: "Actions", key: "actions", render: (_, record) => <Space><Button onClick={() => { setSelectedQuiz({ id: record.id, title: record.title }); setAttemptFilters({ quiz: record.id, studentName: undefined, status: undefined }); setStudentQuery(""); setActiveTab("attempts"); }}>View attempts</Button>{canEditLmsRow(record) && <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />}{canEditLmsRow(record) && <Popconfirm title="Delete this quiz?" onConfirm={() => handleDelete(record)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm>}</Space> },
  ];

  const quizForAttempt = (attempt) => {
    if (!attempt) return null;
    return (
      quizzes.find((quiz) => quiz.id === attempt.quizID) ||
      (selectedQuiz && selectedQuiz.id === attempt.quizID ? selectedQuiz : null)
    );
  };

  const canActOnAttempt = (attempt) => {
    const quiz = quizForAttempt(attempt);
    return !quiz || quiz.mine !== false;
  };

  const canMarkAttempt = (attempt) => {
    const autoMarked = attempt?.isAutoMarked ?? quizForAttempt(attempt)?.isAutoMarked;
    return (
      autoMarked === false &&
      (attempt?.status === "Submitted" || attempt?.status === "Marked") &&
      canActOnAttempt(attempt)
    );
  };

  const openMark = (attempt) => {
    if (!canMarkAttempt(attempt)) {
      message.error("Only the teacher who created or inherited this quiz can mark it.");
      return;
    }
    const values = {};
    (attempt.attempt || []).forEach((entry) => {
      const key = String(entry.questionID);
      const stored = attempt.questionMarks?.[key] ?? attempt.questionMarks?.[entry.questionID];
      values[key] = stored ?? entry.awardedMarks ?? "";
    });
    setMarkValues(values);
    setMarkOverride(attempt.scoreOverride ?? "");
    setEditingQuestions({});
    setMarkAttempt(attempt);
  };

  const entryCap = (entry) => Number(entry?.marks || 0);

  const clampedMarkValue = (entry) => {
    const raw = markValues[String(entry.questionID)];
    if (raw === "" || raw == null) return 0;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return 0;
    const cap = entryCap(entry);
    return cap > 0 ? Math.min(value, cap) : value;
  };

  const snapMark = (entry) => {
    const key = String(entry.questionID);
    const raw = markValues[key];
    if (raw === "" || raw == null) return;
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    const cap = entryCap(entry);
    const label = `Question ${entry.order || entry.questionID}`;
    if (value < 0) {
      setMarkValues((current) => ({ ...current, [key]: 0 }));
      message.warning(`${label} cannot be negative — set to 0.`);
    } else if (cap > 0 && value > cap) {
      setMarkValues((current) => ({ ...current, [key]: cap }));
      message.warning(`${label} marked at ${cap} because the question is worth ${cap} mark${cap === 1 ? "" : "s"}.`);
    }
  };

  const markDerivedPreview = () => {
    if (!markAttempt) return null;
    const entries = markAttempt.attempt || [];
    const total = entries.reduce((sum, entry) => sum + Number(entry.marks || 0), 0);
    if (!total) return null;
    const awarded = entries.reduce((sum, entry) => sum + clampedMarkValue(entry), 0);
    const raw = markOverride !== "" && markOverride != null ? Number(markOverride) : awarded;
    if (!Number.isFinite(raw)) return null;
    const safeRaw = Math.max(0, raw);
    return { awarded, total, percent: ((safeRaw / total) * 100).toFixed(2) };
  };

  const handleMark = async () => {
    if (!markAttempt) return;
    const entries = markAttempt.attempt || [];
    const total = entries.reduce((sum, entry) => sum + Number(entry.marks || 0), 0);
    const questionMarks = {};
    for (const entry of entries) {
      const key = String(entry.questionID);
      const raw = markValues[key];
      if (raw === "" || raw == null) continue;
      const value = Number(raw);
      const cap = entryCap(entry);
      if (!Number.isInteger(value) || value < 0 || (cap > 0 && value > cap)) {
        message.error(
          cap > 0
            ? `Marks for question ${entry.order || entry.questionID} must be a whole number between 0 and ${cap}.`
            : `Marks for question ${entry.order || entry.questionID} must be a whole non-negative number.`
        );
        return;
      }
      questionMarks[key] = value;
    }
    let scoreOverride;
    if (markOverride !== "" && markOverride != null) {
      scoreOverride = Number(markOverride);
      if (!Number.isInteger(scoreOverride) || scoreOverride < 0 || scoreOverride > total) {
        message.error(`Override total must be a whole number between 0 and ${total}.`);
        return;
      }
    }
    if (Object.keys(questionMarks).length === 0 && scoreOverride == null) {
      message.error("Enter marks for at least one question or a total override.");
      return;
    }
    setMarkSaving(true);
    const response = await api.quizAttempts.mark(
      markAttempt.id,
      scoreOverride == null ? { questionMarks } : { questionMarks, scoreOverride },
    );
    setMarkSaving(false);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to save marks"));
      return;
    }
    message.success("Attempt marked");
    setMarkAttempt(null);
    setMarkValues({});
    setMarkOverride("");
    setEditingQuestions({});
    loadAttempts(attemptPagination.current, attemptPagination.pageSize);
  };

  const attemptColumns = [
    { title: "Quiz", dataIndex: "quizTitle", key: "quizTitle" },
    { title: "Learner", dataIndex: "studentName", key: "studentName" },
    { title: "Attempt", dataIndex: "attemptNumber", key: "attemptNumber" },
    { title: "Score", dataIndex: "score", key: "score", render: (value) => value == null ? "—" : `${value}%` },
    { title: "Status", dataIndex: "status", key: "status", render: (value) => <Tag color={statusColor(value)}>{value}</Tag> },
    { title: "Marked by", dataIndex: "markedByName", key: "markedByName", render: (value, record) => value || (record.isAutoMarked && record.status === "Marked" ? "Auto marked" : "—") },
    { title: "Submitted", dataIndex: "submittedAt", key: "submittedAt", render: (value) => formatDateTime(value) },
    { title: "Feedback", dataIndex: "teacherFeedback", key: "teacherFeedback", render: (value) => value || <Text type="secondary">Not added</Text> },
    { title: "Actions", key: "actions", render: (_, record) => <Space><Button icon={<EyeOutlined />} onClick={() => setReviewAttempt(record)}>Review</Button>{canMarkAttempt(record) && <Button icon={<CheckCircleOutlined />} onClick={() => openMark(record)}>Mark</Button>}</Space> },
  ];

  const attemptGroupColumns = [
    { title: "Learner", dataIndex: "studentName", key: "studentName" },
    { title: "Class", dataIndex: "className", key: "className", render: (value) => value || "—" },
    { title: "Attempts", dataIndex: "attemptCount", key: "attemptCount" },
    { title: "Quizzes", dataIndex: "quizCount", key: "quizCount" },
    { title: "Average score", dataIndex: "avgScore", key: "avgScore", render: (value) => value == null ? "—" : `${value}%` },
    { title: "Last attempt", dataIndex: "lastAttemptAt", key: "lastAttemptAt", render: formatDateTime },
  ];

  const contentOptions = useMemo(() => {
    const options = contents.map((item) => ({
      value: item.id,
      label: `${item.title} · ${item.sessionTitle}`,
    }));
    if (
      editing?.contentID &&
      !contents.some((item) => item.id === editing.contentID)
    ) {
      options.push({
        value: editing.contentID,
        label: editing.contentTitle || `Content #${editing.contentID}`,
      });
    }
    return options;
  }, [contents, editing]);

  return (
    <div style={{ padding: 24 }}>
      <LmsPageHeader title="LMS quizzes" description="Build revision quizzes from content, manage questions, and review learner attempts." extra={[<Button key="refresh" icon={<ReloadOutlined />} loading={loading || attemptsLoading} onClick={() => { loadQuizzes(1, quizPagination.pageSize); loadAttempts(1, attemptPagination.pageSize); }}>Refresh</Button>, <Button key="new" type="primary" icon={<PlusOutlined />} onClick={openCreate}>New quiz</Button>]} />
      <LmsErrorAlert error={error} onClose={() => setError("")} />
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[{ key: "quizzes", label: "Quizzes", children: <Table rowKey="id" columns={quizColumns} dataSource={quizzes} loading={loading} scroll={{ x: 1050 }} pagination={{ ...quizPagination, showSizeChanger: false }} onChange={(nextPagination) => loadQuizzes(nextPagination.current, nextPagination.pageSize)} /> }, { key: "attempts", label: "Attempts", children: <><Space wrap style={{ marginBottom: 16 }}><Select allowClear style={{ minWidth: 240 }} placeholder="All quizzes" value={attemptFilters.quiz} onChange={(quizID) => { setSelectedQuiz(quizzes.find((quiz) => quiz.id === quizID) || null); setAttemptFilters((current) => ({ ...current, quiz: quizID })); }} options={[...quizzes, ...(selectedQuiz && !quizzes.some((quiz) => quiz.id === selectedQuiz.id) ? [selectedQuiz] : [])].map((quiz) => ({ value: quiz.id, label: quiz.title }))} /><Input.Search allowClear style={{ width: 240 }} placeholder="Student name" value={studentQuery} onChange={(event) => { const value = event.target.value; setStudentQuery(value); if (!value) setAttemptFilters((current) => ({ ...current, studentName: undefined })); }} onSearch={(value) => setAttemptFilters((current) => ({ ...current, studentName: value ? value.trim() : undefined }))} /><Select allowClear style={{ width: 160 }} placeholder="All statuses" value={attemptFilters.status} onChange={(status) => setAttemptFilters((current) => ({ ...current, status }))} options={["Saved", "Submitted", "Marked"].map((status) => ({ value: status, label: status }))} /></Space><Table rowKey={attemptFilters.quiz ? "id" : "studentID"} columns={attemptFilters.quiz ? attemptColumns : attemptGroupColumns} dataSource={attempts} loading={attemptsLoading} scroll={{ x: 1150 }} expandable={attemptFilters.quiz ? undefined : { expandedRowRender: (group) => <Table rowKey="id" columns={attemptColumns} dataSource={(group.attempts || []).map((attempt) => ({ ...attempt, studentName: group.studentName }))} pagination={false} size="small" scroll={{ x: 1050 }} /> }} pagination={{ ...attemptPagination, showSizeChanger: false }} onChange={(nextPagination) => loadAttempts(nextPagination.current, nextPagination.pageSize)} /></> }]} />
      </Card>

      <Modal title={editing ? "Edit quiz" : "New quiz"} open={formOpen} onCancel={() => { setFormOpen(false); setEditing(null); }} onOk={() => quizForm.submit()} confirmLoading={loading} width={820} destroyOnClose>
        <Form form={quizForm} layout="vertical" onFinish={handleSave}>
          <Row gutter={12}>
            <Col span={14}><Form.Item name="contentID" label="Content" rules={[{ required: true, message: "Select the content this quiz belongs to" }]}><Select showSearch optionFilterProp="label" options={contentOptions} /></Form.Item></Col>
            <Col span={10}><Form.Item name="title" label="Quiz title" rules={[{ required: true, message: "Enter a quiz title" }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="timeLimitMinutes" label="Time limit (minutes)"><InputNumber min={1} style={{ width: "100%" }} placeholder="No limit" /></Form.Item></Col>
            <Col span={8}><Form.Item name="maxAttempts" label="Maximum attempts" rules={[{ required: true }]}><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="isAutoMarked" label="Mark automatically" rules={[{ required: true }]}><Select options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} /></Form.Item></Col>
          </Row>
          <Divider orientation="left">Questions</Divider>
          <Space wrap style={{ marginBottom: 12 }}>
            <input ref={importInputRef} type="file" accept=".xlsx,.xls" onChange={handleQuestionImport} style={{ display: "none" }} />
            <Button onClick={() => importInputRef.current?.click()}>Import questions Excel</Button>
            <Button onClick={downloadQuizTemplate}>Download Excel template</Button>
            <Text type="secondary">One question per row. Use the template columns and review before adding.</Text>
          </Space>
          {importError && <Alert type="error" showIcon message={importError} style={{ marginBottom: 12 }} />}
          {importedQuestions.length > 0 && <Card size="small" title={`Preview ${importedQuestions.length} imported questions`} style={{ marginBottom: 12 }} extra={<Button type="primary" onClick={applyImportedQuestions}>Add to quiz</Button>}>
            {importedQuestions.slice(0, 5).map((question, index) => <div key={index}>{index + 1}. {question.questionText} <Text type="secondary">({question.questionType}, {question.marks} marks)</Text><div><Text type="secondary">{question.optionFields.length > 0 && `Options: ${question.optionFields.join("; ")}. `}Answer: {question.correctAnswer || "Manual marking"}</Text></div></div>)}
            {importedQuestions.length > 5 && <Text type="secondary">and {importedQuestions.length - 5} more</Text>}
          </Card>}
          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Card key={key} size="small" title={`Question ${index + 1}`} extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => { remove(name); setQuestionErrors({}); }} />} style={{ marginBottom: 12, borderColor: questionErrors[index] ? "#ff4d4f" : undefined }}>
                    {questionErrors[index] && <Alert type="error" showIcon message={questionErrors[index]} style={{ marginBottom: 12 }} />}
                    <Form.Item {...restField} name={[name, "id"]} hidden><Input /></Form.Item>
                    <Form.Item {...restField} name={[name, "questionText"]} label="Question" rules={[{ required: true, message: "Enter the question" }]}><Input.TextArea rows={2} /></Form.Item>
                    <Row gutter={12}>
                      <Col xs={24} md={8}><Form.Item {...restField} name={[name, "questionType"]} label="Type" rules={[{ required: true }]}><Select options={LMS_QUESTION_TYPES} /></Form.Item></Col>
                      <Col xs={24} md={8}><Form.Item noStyle shouldUpdate={(previous, current) => previous.questions?.[name]?.questionType !== current.questions?.[name]?.questionType || previous.questions?.[name]?.optionFields !== current.questions?.[name]?.optionFields}>
                        {({ getFieldValue }) => {
                          const question = getFieldValue(["questions", name]) || {};
                          if (question.questionType !== "MCQ") return <Form.Item {...restField} name={[name, "correctAnswer"]} label="Correct answer"><Input placeholder={question.questionType === "True/False" ? "True or False" : "Expected answer"} /></Form.Item>;
                          const options = (question.optionFields || []).map((option) => String(option || "").trim()).filter(Boolean);
                          return <Form.Item {...restField} name={[name, "correctAnswer"]} label="Correct answer" rules={[{ required: true, message: "Choose the correct option" }]}><Select placeholder="Choose the correct option" options={options.map((option, optionIndex) => ({ value: option, label: `${String.fromCharCode(65 + optionIndex)}. ${option}` }))} /></Form.Item>;
                        }}
                      </Form.Item></Col>
                      <Col xs={24} md={4}><Form.Item {...restField} name={[name, "marks"]} label="Marks"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                    </Row>
                    <Form.Item {...restField} name={[name, "order"]} hidden><InputNumber /></Form.Item>
                    <Form.Item noStyle shouldUpdate={(previous, current) => previous.questions?.[name]?.questionType !== current.questions?.[name]?.questionType}>
                      {({ getFieldValue }) => getFieldValue(["questions", name, "questionType"]) === "MCQ" && (
                        <Row gutter={12}>
                          {Array.from({ length: Math.max(4, (getFieldValue(["questions", name, "optionFields"]) || []).length) }, (_, optionIndex) => (
                            <Col xs={24} sm={12} key={optionIndex}>
                              <Form.Item name={[name, "optionFields", optionIndex]} label={`Option ${String.fromCharCode(65 + optionIndex)}`}>
                                <Input placeholder={`Enter option ${String.fromCharCode(65 + optionIndex)}`} />
                              </Form.Item>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </Form.Item>
                  </Card>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add(questionFormValue())}>Add question</Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal title={`Feedback for ${feedbackAttempt?.studentName || "learner"}`} open={Boolean(feedbackAttempt)} onCancel={() => { setFeedbackAttempt(null); feedbackForm.resetFields(); }} onOk={() => feedbackForm.submit()} destroyOnClose>
        <Form form={feedbackForm} layout="vertical" onFinish={handleFeedback}><Form.Item name="teacherFeedback" label="Teacher feedback" rules={[{ required: true, message: "Enter feedback" }]}><Input.TextArea rows={5} /></Form.Item></Form>
      </Modal>

      <Modal title={reviewAttempt ? `Attempt ${reviewAttempt.attemptNumber} · ${reviewAttempt.studentName || "learner"}` : "Attempt review"} open={Boolean(reviewAttempt)} onCancel={() => setReviewAttempt(null)} width={760} destroyOnClose footer={[<Button key="close" onClick={() => setReviewAttempt(null)}>Close</Button>, ...(canMarkAttempt(reviewAttempt) ? [<Button key="mark" type="primary" onClick={() => { const attempt = reviewAttempt; setReviewAttempt(null); openMark(attempt); }}>Mark this attempt</Button>] : [])]}>
        {(reviewAttempt?.attempt || []).map((entry, index) => {
          const cap = Number(entry.marks || 0);
          // What the teacher awarded wins over the string-match check, so a
          // manually marked attempt reads the same here as on the mark screen.
          let effective = entry.isCorrect;
          if (entry.awardedMarks != null) {
            const awarded = Number(entry.awardedMarks);
            if (cap > 0) effective = awarded >= cap ? true : awarded <= 0 ? false : "partial";
            else effective = awarded > 0;
          }
          return (
            <Card key={entry.questionID ?? index} size="small" style={{ marginBottom: 12 }} title={<Space>Question {entry.order || index + 1}<Tag color="blue" style={{ fontSize: 14, padding: "2px 10px" }}>{cap > 0 ? `${cap} mark${cap === 1 ? "" : "s"}` : "No preset max"}</Tag></Space>}>
              <Text strong>{entry.questionText}</Text>
              <div><Text type="secondary">Learner answer: </Text><Text>{entry.learnerAnswer ?? "Unanswered"}</Text></div>
              {entry.correctAnswer != null && <div><Text type="secondary">Correct answer: </Text><Text>{entry.correctAnswer}</Text></div>}
              {(entry.correctAnswer != null || entry.awardedMarks != null) && (
                <div>{effective == null ? <Tag>Not auto-checked</Tag> : effective === "partial" ? <Tag color="gold">Partially correct</Tag> : <Tag color={effective ? "green" : "red"}>{effective ? "Correct" : "Incorrect"}</Tag>}{entry.awardedMarks != null && <Text type="secondary"> · Awarded {entry.awardedMarks}</Text>}</div>
              )}
            </Card>
          );
        })}
        {!(reviewAttempt?.attempt || []).length && <Text type="secondary">No per-question breakdown for this attempt.</Text>}
        <Divider>Feedback</Divider>
        <Text>{reviewAttempt?.teacherFeedback || "No feedback yet."}</Text>
        {reviewAttempt && canActOnAttempt(reviewAttempt) && (
          <div style={{ marginTop: 8 }}>
            <Button icon={<MessageOutlined />} onClick={() => openFeedbackFor(reviewAttempt)}>Add/Edit feedback</Button>
          </div>
        )}
      </Modal>

      <Modal title={markAttempt ? `Mark attempt ${markAttempt.attemptNumber} · ${markAttempt.studentName || "learner"}` : "Mark attempt"} open={Boolean(markAttempt)} onCancel={() => { setMarkAttempt(null); setMarkValues({}); setMarkOverride(""); setEditingQuestions({}); }} onOk={handleMark} confirmLoading={markSaving} okText="Save marks" width={760} destroyOnClose>
        {(markAttempt?.attempt || []).map((entry, index) => {
          const cap = entryCap(entry);
          const key = String(entry.questionID);
          const saved = entry.awardedMarks ?? markAttempt.questionMarks?.[key] ?? markAttempt.questionMarks?.[entry.questionID];
          const isMarked = saved != null;
          const editing = !isMarked || !!editingQuestions[key];
          const savedNum = isMarked ? Number(saved) : null;
          const verdict = !isMarked
            ? null
            : cap > 0
              ? (savedNum >= cap ? true : savedNum <= 0 ? false : "partial")
              : (savedNum > 0);
          const raw = markValues[key];
          const hasAwardedValue = raw !== "" && raw != null;
          const awardedIsZero = hasAwardedValue && Number(raw) === 0;
          // Correct obeys a typed value and never replaces it; it only fills
          // an empty field. A zero award hides Correct so only Wrong shows.
          const showCorrect = cap > 0 && !awardedIsZero;
          return (
            <Card key={entry.questionID ?? index} size="small" style={{ marginBottom: 12 }} title={<Space>Question {entry.order || index + 1}<Tag color="blue" style={{ fontSize: 14, padding: "2px 10px" }}>{cap > 0 ? `${cap} mark${cap === 1 ? "" : "s"}` : "No preset max"}</Tag></Space>}>
              <Text strong>{entry.questionText}</Text>
              <div><Text type="secondary">Learner answer: </Text><Text>{entry.learnerAnswer ?? "Unanswered"}</Text></div>
              {entry.correctAnswer != null && <div><Text type="secondary">Correct answer: </Text><Text>{entry.correctAnswer}</Text></div>}
              {isMarked && !editing ? (
                <div style={{ marginTop: 8 }}>
                  <Space>
                    {verdict === true ? <Tag color="green" icon={<CheckOutlined />}>Marked correct</Tag>
                      : verdict === false ? <Tag color="red" icon={<CloseOutlined />}>Marked wrong</Tag>
                      : verdict === "partial" ? <Tag color="gold">Marked partial</Tag>
                      : <Tag>Marked</Tag>}
                    <Text type="secondary">Awarded {savedNum}{cap > 0 ? ` of ${cap}` : ""}</Text>
                    <Button size="small" type="link" onClick={() => setEditingQuestions((current) => ({ ...current, [key]: true }))}>Change</Button>
                  </Space>
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Awarded marks: </Text>
                  <InputNumber min={0} precision={0} style={{ width: 120 }} value={markValues[key] ?? ""} onChange={(value) => setMarkValues((current) => ({ ...current, [key]: value ?? "" }))} onKeyDown={(event) => { if (event.key === "-") event.preventDefault(); }} onBlur={() => snapMark(entry)} />
                  {showCorrect && <Button size="small" icon={<CheckOutlined />} style={{ marginLeft: 8 }} title="Fill full marks, or keep the value you typed" onMouseDown={(event) => event.preventDefault()} onClick={() => {
                    const current = markValues[key];
                    if (current === "" || current == null) {
                      setMarkValues((prev) => ({ ...prev, [key]: cap }));
                    } else if (Number(current) < 0) {
                      setMarkValues((prev) => ({ ...prev, [key]: 0 }));
                    }
                    // Otherwise obey the typed value: leave it untouched.
                  }}>Correct</Button>}
                  <Button size="small" danger icon={<CloseOutlined />} style={{ marginLeft: 4 }} onClick={() => setMarkValues((current) => ({ ...current, [key]: 0 }))}>Wrong</Button>
                </div>
              )}
            </Card>
          );
        })}
        {!(markAttempt?.attempt || []).length && <Text type="secondary">No per-question breakdown for this attempt.</Text>}
        <Row gutter={12}>
          <Col span={12}><Text type="secondary">Override total (optional — works on its own)</Text><InputNumber min={0} precision={0} style={{ width: "100%" }} placeholder="Leave blank for derived total" value={markOverride} onChange={(value) => setMarkOverride(value ?? "")} onKeyDown={(event) => { if (event.key === "-") event.preventDefault(); }} onBlur={() => { if (markOverride !== "" && markOverride != null && Number(markOverride) < 0) { setMarkOverride(0); message.warning("Override total cannot be negative — set to 0."); } }} /></Col>
          <Col span={12}>{(() => { const preview = markDerivedPreview(); return preview ? <Alert type="info" showIcon message={`Total ${preview.awarded} of ${preview.total} · ${preview.percent}%`} /> : null; })()}</Col>
        </Row>
        {markAttempt?.markedByName && <Text type="secondary">Last marked by {markAttempt.markedByName}{markAttempt.markedAt ? ` · ${formatDateTime(markAttempt.markedAt)}` : ""}</Text>}
        <Divider>Feedback</Divider>
        <Text>{markAttempt?.teacherFeedback || "No feedback yet."}</Text>
        <div style={{ marginTop: 8 }}>
          <Button icon={<MessageOutlined />} onClick={() => openFeedbackFor(markAttempt)}>Add/Edit feedback</Button>
        </div>
      </Modal>
    </div>
  );
};

export default LmsQuizzes;
